import React from 'react';
import { Loader2, ListOrdered } from 'lucide-react';
import { Button } from '../../../../../components/ui/button';
import { Input } from '../../../../../components/ui/input';
import {
  extractSubmitErrorMessage,
  getCompatibilityReportSequence,
} from '../../../api/compatibilityReportsApi';
import type { CompatSequenceResponse } from '../types';

export interface ArrivalOrderState {
  runIndexInput: string;
  loading: boolean;
  error: string | null;
  sequence: CompatSequenceResponse | null;
}

export const EMPTY_ARRIVAL_ORDER_STATE: ArrivalOrderState = {
  runIndexInput: '0',
  loading: false,
  error: null,
  sequence: null,
};

interface ArrivalOrderPanelProps {
  reportId: string | null;
  /** Inclusive max run index = orders - 1. */
  maxRunIndex: number;
  /** Kept in the parent modal so tab switches do not clear a loaded order. */
  state: ArrivalOrderState;
  onStateChange: (next: ArrivalOrderState) => void;
}

/**
 * Fetches GET .../compatibility-reports/{id}/sequence/{runIndex}.
 * Never recomputes the shuffle in the browser (PRD §6).
 * State is controlled by the parent so leaving Overview and coming back keeps the result.
 */
export const ArrivalOrderPanel: React.FC<ArrivalOrderPanelProps> = ({
  reportId,
  maxRunIndex,
  state,
  onStateChange,
}) => {
  const { runIndexInput, loading, error, sequence } = state;

  const patch = (partial: Partial<ArrivalOrderState>) => {
    onStateChange({ ...state, ...partial });
  };

  const canFetch =
    Boolean(reportId) &&
    reportId !== 'sync' &&
    maxRunIndex >= 0 &&
    !loading;

  const handleFetch = async () => {
    if (!reportId || reportId === 'sync') {
      patch({
        error: 'No stored report id — sequence replay needs a job/report id from the API.',
      });
      return;
    }
    const runIndex = Number.parseInt(runIndexInput, 10);
    if (!Number.isFinite(runIndex) || runIndex < 0 || runIndex > maxRunIndex) {
      patch({ error: `runIndex must be a whole number from 0 to ${maxRunIndex}.` });
      return;
    }
    patch({ loading: true, error: null });
    try {
      const data = await getCompatibilityReportSequence(reportId, runIndex);
      onStateChange({
        ...state,
        loading: false,
        error: null,
        sequence: data,
        runIndexInput: String(runIndex),
      });
    } catch (err) {
      onStateChange({
        ...state,
        loading: false,
        sequence: null,
        error: extractSubmitErrorMessage(err),
      });
    }
  };

  return (
    <div className="rounded-xl border border-[#E2E5EA] bg-white p-5 relative overflow-hidden">
      <div className="absolute top-0 left-0 right-0 h-[3px] bg-blueprint-blue" />
      <p className="text-[11px] font-semibold tracking-wider uppercase text-heritage-grey mb-1">
        Arrival order for a specific run
      </p>
      <div className="flex flex-col sm:flex-row sm:items-end gap-3 mb-4">
        <div className="flex-1 min-w-0">
          <label className="block text-[12.5px] font-semibold text-blueprint-blue mb-1.5">
            Run index (0 – {Math.max(0, maxRunIndex)})
          </label>
          <Input
            type="number"
            min={0}
            max={maxRunIndex}
            step={1}
            value={runIndexInput}
            onChange={(e) => patch({ runIndexInput: e.target.value })}
            disabled={!reportId || reportId === 'sync'}
            className="h-9"
          />
        </div>
        <Button
          type="button"
          onClick={handleFetch}
          disabled={!canFetch}
          className="h-9 bg-hero-red hover:bg-hero-red/90 shrink-0"
        >
          {loading ? (
            <>
              <Loader2 className="h-4 w-4 animate-spin mr-2" /> Loading…
            </>
          ) : (
            <>
              <ListOrdered className="h-4 w-4 mr-2" /> Load order
            </>
          )}
        </Button>
      </div>

      {(!reportId || reportId === 'sync') && (
        <div className="border-l-[3px] border-[#E6A100] bg-[rgba(230,161,0,0.10)] rounded-r-lg px-4 py-3 text-[13.5px] text-text-grey mb-3">
          Sequence replay needs a real report/job id from the deployed API (not a sync-only
          response).
        </div>
      )}

      {error && (
        <div className="rounded-lg border border-[rgba(235,28,55,0.25)] bg-[rgba(235,28,55,0.06)] p-3 text-[13px] text-hero-red mb-3">
          {error}
        </div>
      )}

      {sequence && (
        <div className="space-y-3">
          <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
            <div className="bg-page-bg border border-[#E2E5EA] rounded-[10px] p-3 text-center">
              <div className="font-heading font-bold text-[20px] text-blueprint-blue">
                {sequence.runIndex}
              </div>
              <div className="text-[10px] font-semibold tracking-wide uppercase text-heritage-grey mt-1">
                Run index
              </div>
            </div>
            <div className="bg-page-bg border border-[#E2E5EA] rounded-[10px] p-3 text-center">
              <div className="font-heading font-bold text-[20px] text-blueprint-blue tabular-nums">
                {sequence.seed}
              </div>
              <div className="text-[10px] font-semibold tracking-wide uppercase text-heritage-grey mt-1">
                Seed
              </div>
            </div>
            <div className="bg-page-bg border border-[#E2E5EA] rounded-[10px] p-3 text-center">
              <div className="font-heading font-bold text-[20px] text-blueprint-blue">
                {sequence.order.length}
              </div>
              <div className="text-[10px] font-semibold tracking-wide uppercase text-heritage-grey mt-1">
                Pipes in order
              </div>
            </div>
          </div>

          <div className="max-h-[320px] overflow-auto border border-[#E2E5EA] rounded-[10px]">
            <table className="w-full table-fixed border-collapse text-[13.5px]">
              <thead>
                <tr>
                  <th className="sticky top-0 bg-page-bg px-3 py-2 text-center text-[10px] font-bold tracking-wide uppercase text-heritage-grey border-b border-[#E2E5EA] w-[30%]">
                    #
                  </th>
                  <th className="sticky top-0 bg-page-bg px-3 py-2 text-center text-[10px] font-bold tracking-wide uppercase text-heritage-grey border-b border-[#E2E5EA]">
                    Pipe ID
                  </th>
                </tr>
              </thead>
              <tbody>
                {sequence.order.map((pipeId, i) => (
                  <tr key={`${pipeId}-${i}`} className="border-b border-[#EEF0F3] last:border-b-0">
                    <td className="px-3 py-1.5 text-center text-heritage-grey tabular-nums">
                      {i + 1}
                    </td>
                    <td className="px-3 py-1.5 text-center">
                      <span className="font-semibold text-blueprint-blue">{pipeId}</span>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}
    </div>
  );
};
